import React, { useState } from 'react';
import { Box, IconButton, Slider, Typography, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Replay5Icon from '@mui/icons-material/Replay5';
import Forward5Icon from '@mui/icons-material/Forward5';

const BASELINE = 10000;

function formatTime(timelineTime, leadIn) {
  const raceSecs = timelineTime - BASELINE - leadIn;
  const display = Math.max(0, raceSecs);
  const m = Math.floor(display / 60);
  const s = Math.floor(display % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Playback controls for the BroadcastViewer.
 *
 * Props:
 *   isPlaying      - boolean
 *   timelineTime   - current timeline position (starts at BASELINE)
 *   duration       - estimated max timeline time (BASELINE + race length in seconds)
 *   onPlay
 *   onPause
 *   onScrub        - callback(newTimelineTime)
 *   onSkip         - callback(seconds) e.g. -5 or +5
 */
function BroadcastControls({
  isPlaying,
  timelineTime,
  duration = BASELINE + 60 * 30,
  leadIn = 15,
  onPlay,
  onPause,
  onScrub,
  onSkip,
}) {
  const [dragValue, setDragValue] = useState(null);

  const currentLabel = formatTime(dragValue ?? timelineTime, leadIn);
  const durationLabel = formatTime(duration, leadIn);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        bgcolor: 'grey.900',
        borderRadius: 1,
      }}
    >
      {/* Skip back */}
      <Tooltip title="Back 5s">
        <IconButton size="small" onClick={() => onSkip?.(-5)} sx={{ color: 'white' }}>
          <Replay5Icon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Play / Pause */}
      <IconButton
        onClick={isPlaying ? onPause : onPlay}
        sx={{ color: 'white' }}
      >
        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>

      {/* Skip forward */}
      <Tooltip title="Forward 5s">
        <IconButton size="small" onClick={() => onSkip?.(5)} sx={{ color: 'white' }}>
          <Forward5Icon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Current time */}
      <Typography variant="caption" color="grey.400" sx={{ minWidth: 40 }}>
        {currentLabel}
      </Typography>

      {/* Scrubber */}
      <Slider
        value={dragValue ?? timelineTime}
        min={BASELINE}
        max={duration}
        step={0.5}
        onChange={(_, val) => setDragValue(val)}
        onChangeCommitted={(_, val) => {
          setDragValue(null);
          onScrub?.(val);
        }}
        sx={{
          flex: 1,
          color: 'success.main',
          '& .MuiSlider-thumb': { width: 12, height: 12 },
        }}
      />

      {/* Duration */}
      <Typography variant="caption" color="grey.400" sx={{ minWidth: 40 }}>
        {durationLabel}
      </Typography>
    </Box>
  );
}

export default BroadcastControls;
